import Add from '@mui/icons-material/Add'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material'
import type { TemplateManual } from '@ulysses-inc/harami_api_client'
import type { FC } from 'react'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  addTemplateManual as addTemplateManualAction,
  deleteTemplateManual as deleteTemplateManualAction,
  updateTemplateManual as updateTemplateManualAction,
} from 'src/state/ducks/templates/editTemplate/actions'
import type { RootState } from 'src/state/store'
import EditManualBlocks from './EditManualBlocks'

const ManualNameCell: FC<{ manual: TemplateManual }> = ({ manual }) => {
  const dispatch = useDispatch()
  const [focused, setFocused] = useState(false)
  const [value, setValue] = useState(manual.name ?? '')

  const handleBlur = () => {
    setFocused(false)
    dispatch(updateTemplateManualAction({ ...manual, name: value }))
  }

  return (
    <Tooltip title={!focused ? value : ''} placement="top">
      <TextField
        inputProps={{ 'aria-label': 'マニュアル名' }}
        value={value}
        onChange={e => setValue(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        multiline
        {...(!focused && { rows: 1 })}
        fullWidth
        size="small"
      />
    </Tooltip>
  )
}

const ManualDeleteButton: FC<{ manual: TemplateManual }> = ({ manual }) => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)

  return (
    <>
      <Tooltip title="マニュアルを削除">
        <IconButton
          color="error"
          onClick={() => setOpen(true)}
          aria-label="マニュアルを削除"
        >
          <DeleteOutline color="error" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>マニュアルを削除してもよろしいですか？</DialogTitle>
        <DialogActions>
          <Button variant="outlined" onClick={() => setOpen(false)}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              dispatch(deleteTemplateManualAction(manual))
              setOpen(false)
            }}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const EditManualsTable = () => {
  const dispatch = useDispatch()

  const templateManuals = useSelector(
    (state: RootState) => state.templatesState.editTemplate.manuals,
  )

  return (
    <>
      {templateManuals.length > 0 && (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                size="small"
                sx={{ width: '60%', backgroundColor: 'white' }}
              >
                マニュアル名
              </TableCell>
              <TableCell
                size="small"
                sx={{ width: '30%', backgroundColor: 'white' }}
              >
                内容を編集
              </TableCell>
              <TableCell
                size="small"
                sx={{ width: '10%', backgroundColor: 'white' }}
              >
                削除
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: 'white' }}>
            {templateManuals.map(manual => (
              <TableRow key={manual.id}>
                <TableCell size="small">
                  <ManualNameCell manual={manual} />
                </TableCell>
                <TableCell size="small">
                  <EditManualBlocks manual={manual} />
                </TableCell>
                <TableCell size="small">
                  <ManualDeleteButton manual={manual} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Button
        startIcon={<Add />}
        variant="text"
        onClick={() => dispatch(addTemplateManualAction())}
        sx={{ mx: 2, mt: 1 }}
        data-testid="add-manual-button"
      >
        追加
      </Button>
    </>
  )
}

export default EditManualsTable
